import {
  Table,
  NumberFormatter,
  Paper,
  SimpleGrid,
  Slider,
  Text,
  Box,
  Flex,
  LoadingOverlay,
} from "@mantine/core";
import classes from "./index.module.css";
import { useAdviceInputStore, useUpdateCustomerData } from "../../hooks/ui";
import { useGetCalculationById } from "../../hooks/api/useGetCalculationById";
import { useUpdateCalculationById } from "../../hooks/api/useUpdateCalculationById";
import { useCallback, useRef } from "react";
import { isFullCredentials } from "../../hooks/ui/customerDataStore";
import { isMobileDevice } from "../../utils/useDeviceDetection";
import { useDisclosure } from "@mantine/hooks";
import AdviceLockedModal from "../adviceLockedModal";
import { REPLAY_BLOCK_CLASS } from "../../utils/statsig";

const isMobile = isMobileDevice();

export function PensionForecastTable({
  hasLockedAdvice,
}: {
  hasLockedAdvice: boolean;
}) {
  const { customerData } = useUpdateCustomerData();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { data, isFetching } = useGetCalculationById({
    credentials: customerData.credentials,
    enabled: isFullCredentials(customerData.credentials),
  });

  const {
    retirementAge,
    riskPreference,
    updateRetirementAge,
    updateRiskPreference,
  } = useAdviceInputStore((state) => state);

  // Store the previous values before any slider change
  const previousValuesRef = useRef<{
    retirementAge: number;
    riskPreference: number;
  } | null>(null);
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);

  const handleAdviceLocked = useCallback(() => {
    // Revert to previous values when advice is locked
    if (previousValuesRef.current) {
      updateRetirementAge(previousValuesRef.current.retirementAge);
      updateRiskPreference(previousValuesRef.current.riskPreference);
    }
    openModal();
  }, [openModal, updateRetirementAge, updateRiskPreference]);

  const updateCalculation = useUpdateCalculationById({
    onAdviceLocked: handleAdviceLocked,
  });

  const storePreviousValues = useCallback(() => {
    if (!previousValuesRef.current) {
      previousValuesRef.current = {
        retirementAge: useAdviceInputStore.getState().retirementAge,
        riskPreference: useAdviceInputStore.getState().riskPreference,
      };
    }
  }, []);

  const handleRetirementAgeChange = useCallback(
    (value: number) => {
      storePreviousValues();
      updateRetirementAge(value);
    },
    [storePreviousValues, updateRetirementAge],
  );

  const handleRiskPreferenceChange = useCallback(
    (value: number) => {
      storePreviousValues();
      updateRiskPreference(value);
    },
    [storePreviousValues, updateRiskPreference],
  );

  const debouncedMutation = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const {
        retirementAge: currentRetirementAge,
        riskPreference: currentRiskPreference,
      } = useAdviceInputStore.getState();

      updateCalculation.mutate(
        {
          credentials: customerData.credentials,
          params: {
            zAge: currentRetirementAge,
            riskPreference: currentRiskPreference,
          },
        },
        {
          onSuccess: () => {
            previousValuesRef.current = null;
          },
        },
      );
    }, 800);
  }, [updateCalculation, customerData.credentials]);

  if (!data) return null;

  const textColor = hasLockedAdvice
    ? "var(--wallet-color-neutrals-gray-500)"
    : "black";

  return (
    <>
      <AdviceLockedModal opened={modalOpened} close={closeModal} />
      <Paper className={classes.summaryPaper} pos="relative">
        <LoadingOverlay
          visible={isFetching}
          overlayProps={{ radius: "lg", blur: 2 }}
        />
        <Table className={classes.summaryTable}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th
                scope="col"
                bg={"var(--wallet-color-sambla-green-shade-200)"}
              />
              <Table.Th
                scope="col"
                bg={"var(--wallet-color-sambla-green-shade-200)"}
              >
                Nuvarande
              </Table.Th>
              <Table.Th
                scope="col"
                bg={"var(--wallet-color-sambla-green-shade-300)"}
              >
                Enligt råd
              </Table.Th>
              <Table.Th
                scope="col"
                bg={"var(--wallet-color-sambla-green-shade-200)"}
              >
                Skillnad
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Th
                scope="row"
                bg={isMobile ? "" : "var(--wallet-color-neutrals-gray-100)"}
              >
                Bättre utfall
              </Table.Th>
              <Table.Td data-label="Nuvarande">
                <NumberFormatter
                  className={REPLAY_BLOCK_CLASS}
                  value={data?.summaryTable?.expectedHighYieldPension ?? 0}
                  decimalScale={0}
                  thousandSeparator={" "}
                />
              </Table.Td>
              <Table.Td data-label="Enligt råd">
                <Text fw={700}>
                  <NumberFormatter
                    className={REPLAY_BLOCK_CLASS}
                    value={data?.summaryTable?.advisedHighYieldPension ?? 0}
                    decimalScale={0}
                    thousandSeparator={" "}
                  />
                </Text>
              </Table.Td>
              <Table.Td
                data-label="Skillnad"
                c={
                  (data?.summaryTable?.diffHighYieldPension ?? 0) > 0
                    ? "var(--wallet-color-sambla-green-shade-700)"
                    : "var(--wallet-color-sambla-red-shade-700)"
                }
              >
                <NumberFormatter
                  className={REPLAY_BLOCK_CLASS}
                  value={data?.summaryTable?.diffHighYieldPension ?? 0}
                  decimalScale={0}
                  thousandSeparator={" "}
                  prefix={
                    (data?.summaryTable?.diffHighYieldPension ?? 0) > 0
                      ? "+ "
                      : ""
                  }
                />
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th
                scope="row"
                bg={isMobile ? "" : "var(--wallet-color-neutrals-gray-100)"}
              >
                Förväntad pension
              </Table.Th>
              <Table.Td data-label="Nuvarande">
                <NumberFormatter
                  className={REPLAY_BLOCK_CLASS}
                  value={data?.summaryTable?.expectedPension ?? 0}
                  decimalScale={0}
                  thousandSeparator={" "}
                />
              </Table.Td>
              <Table.Td data-label="Enligt råd">
                <Text fw={700}>
                  <NumberFormatter
                    className={REPLAY_BLOCK_CLASS}
                    value={data?.summaryTable?.advisedExpectedPension ?? 0}
                    decimalScale={0}
                    thousandSeparator={" "}
                  />
                </Text>
              </Table.Td>
              <Table.Td
                data-label="Skillnad"
                c={
                  (data?.summaryTable?.diffExpectedPension ?? 0) > 0
                    ? "var(--wallet-color-sambla-green-shade-700)"
                    : "var(--wallet-color-sambla-red-shade-700)"
                }
              >
                <NumberFormatter
                  className={REPLAY_BLOCK_CLASS}
                  value={data?.summaryTable?.diffExpectedPension ?? 0}
                  decimalScale={0}
                  thousandSeparator={" "}
                  prefix={
                    (data?.summaryTable?.diffExpectedPension ?? 0) > 0
                      ? "+ "
                      : ""
                  }
                />
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th
                scope="row"
                bg={isMobile ? "" : "var(--wallet-color-neutrals-gray-100)"}
              >
                Sämre utfall
              </Table.Th>
              <Table.Td data-label="Nuvarande">
                <NumberFormatter
                  className={REPLAY_BLOCK_CLASS}
                  value={data?.summaryTable?.expectedLowYieldPension ?? 0}
                  decimalScale={0}
                  thousandSeparator={" "}
                />
              </Table.Td>
              <Table.Td data-label="Enligt råd">
                <Text fw={700}>
                  <NumberFormatter
                    className={REPLAY_BLOCK_CLASS}
                    value={data?.summaryTable?.advisedLowYieldPension ?? 0}
                    decimalScale={0}
                    thousandSeparator={" "}
                  />
                </Text>
              </Table.Td>
              <Table.Td
                data-label="Skillnad"
                c={
                  (data?.summaryTable?.diffLowYieldPension ?? 0) > 0
                    ? "var(--wallet-color-sambla-green-shade-700)"
                    : "var(--wallet-color-sambla-red-shade-700)"
                }
              >
                <NumberFormatter
                  className={REPLAY_BLOCK_CLASS}
                  value={data?.summaryTable?.diffLowYieldPension ?? 0}
                  decimalScale={0}
                  thousandSeparator={" "}
                  prefix={
                    (data?.summaryTable?.diffLowYieldPension ?? 0) > 0
                      ? "+ "
                      : ""
                  }
                />
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>

        <SimpleGrid
          cols={{ base: 1, sm: 2 }}
          spacing={{ base: "xl" }}
          mt={"lg"}
          pb={"lg"}
          px={"xl"}
        >
          <Box>
            <Flex
              mb={"sm"}
              align={"center"}
              justify={"space-between"}
              c={
                hasLockedAdvice
                  ? "var(--wallet-color-neutrals-gray-500)"
                  : "var(--wallet-color-primary-700)"
              }
            >
              <Text c={textColor}>Pensionsålder</Text>{" "}
              <Text c={textColor} size="xl" fw={700}>
                {retirementAge}
              </Text>
            </Flex>
            <Slider
              color="var(--mantine-primary-color-filled)"
              size="lg"
              mb={"xl"}
              min={55}
              max={78}
              value={retirementAge}
              disabled={hasLockedAdvice}
              onChange={handleRetirementAgeChange}
              onChangeEnd={debouncedMutation}
              marks={[
                { value: 55, label: "55 år" },
                { value: 78, label: "78 år" },
              ]}
            />
          </Box>
          <Box>
            <Flex mb={"sm"} align={"center"} justify={"space-between"}>
              <Text c={textColor}>Mer eller mindre aktier</Text>
              <Text c={textColor} size="xl" fw={700}>
                {Math.floor(riskPreference - 61)}
              </Text>
            </Flex>

            <Slider
              color="var(--mantine-primary-color-filled)"
              size="lg"
              mb={"xl"}
              min={1}
              max={121}
              value={riskPreference}
              onChange={handleRiskPreferenceChange}
              onChangeEnd={debouncedMutation}
              label={(value) => `${Math.floor(value - 61)}`}
              marks={[
                { value: 1, label: "-60" },
                { value: 61, label: "0" },
                { value: 121, label: "+60" },
              ]}
              disabled={hasLockedAdvice}
            />
          </Box>
        </SimpleGrid>
      </Paper>
    </>
  );
}
